import { ArrowDownIcon, ArrowUpIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useEffect, useRef, useState, type RefObject } from 'react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { type TemporalLocale } from './locale';
import { MonthYearChooser } from './month-year-chooser';
import {
  addDays,
  addMonths,
  addMonthsToMonth,
  clampDate,
  daysInMonth,
  formatDate,
  isDateInRange,
  isMonthInRange,
  monthGrid,
  sameDate,
  startOfWeek,
  type CalendarDate,
  type CalendarMonth,
} from './temporal';

/** How a day was chosen: an arrow move (live, the popup stays), a click or Space, or Enter. */
export type DaySelectIntent = 'move' | 'pick' | 'enter';

export interface CalendarPanelProps {
  locale: TemporalLocale;
  selected: CalendarDate | null;
  today: CalendarDate;
  min: CalendarDate | null;
  max: CalendarDate | null;
  onSelect: (date: CalendarDate, intent: DaySelectIntent) => void;
  /** Filled with a function that focuses the active day (the popup's initial focus). */
  focusRef: RefObject<(() => void) | null>;
  /** Filled with the panel's Escape handler: leaves the month chooser, else returns false. */
  escapeRef: RefObject<(() => boolean) | null>;
  className?: string;
}

const monthOf = (date: CalendarDate): CalendarMonth => ({ year: date.year, month: date.month });

/**
 * Chrome's calendar: a month-and-year button that swaps the day grid for the year list, previous
 * and next month arrows, and a six-week grid in the locale's week order with today marked and the
 * selection filled. Arrow keys move the selection (and the value) live, as Chrome does.
 */
export function CalendarPanel({
  locale,
  selected,
  today,
  min,
  max,
  onSelect,
  focusRef,
  escapeRef,
  className,
}: CalendarPanelProps) {
  const { t } = useTranslation('common');
  const [active, setActive] = useState(() => clampDate(selected ?? today, min, max));
  const [display, setDisplay] = useState(() => monthOf(active));
  const [view, setView] = useState<'days' | 'months'>('days');
  const gridRef = useRef<HTMLTableElement>(null);
  const viewButtonRef = useRef<HTMLButtonElement>(null);
  const focusPending = useRef(false);

  const focusActive = () => {
    gridRef.current
      ?.querySelector<HTMLButtonElement>('button[tabindex="0"]')
      ?.focus({ preventScroll: true });
  };

  useEffect(() => {
    focusRef.current = focusActive;
    escapeRef.current = () => {
      if (view !== 'months') return false;
      setView('days');
      focusPending.current = true;
      return true;
    };
  });

  // After a keyboard move or leaving the chooser, focus follows the active day.
  useEffect(() => {
    if (!focusPending.current || view !== 'days') return;
    focusPending.current = false;
    focusActive();
  }, [active, display, view]);

  const prevMonth = addMonthsToMonth(display, -1);
  const nextMonth = addMonthsToMonth(display, 1);
  const canPrev = isMonthInRange(prevMonth, min, max);
  const canNext = isMonthInRange(nextMonth, min, max);

  const showMonth = (month: CalendarMonth) => {
    setDisplay(month);
    // The focusable day follows into the shown month; the value does not change.
    const day = Math.min(active.day, daysInMonth(month.year, month.month));
    setActive(clampDate({ ...month, day }, min, max));
  };

  const moveTo = (target: CalendarDate) => {
    const next = clampDate(target, min, max);
    focusPending.current = true;
    setActive(next);
    setDisplay(monthOf(next));
    onSelect(next, 'move');
  };

  const onGridKeyDown = (event: React.KeyboardEvent) => {
    const { key, shiftKey } = event;
    const moves: Record<string, () => CalendarDate> = {
      ArrowLeft: () => addDays(active, -1),
      ArrowRight: () => addDays(active, 1),
      ArrowUp: () => addDays(active, -7),
      ArrowDown: () => addDays(active, 7),
      PageUp: () => addMonths(active, shiftKey ? -12 : -1),
      PageDown: () => addMonths(active, shiftKey ? 12 : 1),
      Home: () => startOfWeek(active, locale.weekStart),
      End: () => addDays(startOfWeek(active, locale.weekStart), 6),
    };
    const move = moves[key];
    if (move) {
      event.preventDefault();
      moveTo(move());
      return;
    }
    if (key === 'Enter') {
      event.preventDefault();
      if (isDateInRange(active, min, max)) onSelect(active, 'enter');
    }
  };

  const weeks = monthGrid(display, locale.weekStart);
  const weekdayOrder = Array.from({ length: 7 }, (_, i) => (locale.weekStart + i) % 7);
  const monthYear = locale.formatMonthYear(display.year, display.month);

  return (
    <div data-slot="calendar" className={cn('flex w-[15.5rem] flex-col p-2', className)}>
      <div className="flex items-center justify-between gap-1 pb-1">
        <Button
          ref={viewButtonRef}
          type="button"
          variant="ghost"
          size="sm"
          className="px-2 font-medium"
          aria-expanded={view === 'months'}
          aria-label={t('dateTime.showMonthsAndYears', { monthYear })}
          onClick={() => {
            if (view === 'months') focusPending.current = true;
            setView(view === 'days' ? 'months' : 'days');
          }}
        >
          {monthYear}
          {view === 'days' ? (
            <ChevronDownIcon aria-hidden className="text-muted-foreground" />
          ) : (
            <ChevronUpIcon aria-hidden className="text-muted-foreground" />
          )}
        </Button>
        {view === 'days' ? (
          <div className="flex items-center">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={t('dateTime.previousMonth')}
              disabled={!canPrev}
              onClick={() => showMonth(prevMonth)}
            >
              <ArrowUpIcon aria-hidden />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={t('dateTime.nextMonth')}
              disabled={!canNext}
              onClick={() => showMonth(nextMonth)}
            >
              <ArrowDownIcon aria-hidden />
            </Button>
          </div>
        ) : null}
      </div>
      {view === 'months' ? (
        <MonthYearChooser
          locale={locale}
          display={display}
          today={today}
          min={min}
          max={max}
          onPick={(month) => {
            focusPending.current = true;
            showMonth(month);
            setView('days');
          }}
        />
      ) : (
        <table
          ref={gridRef}
          role="grid"
          aria-label={monthYear}
          className="w-full border-collapse"
          onKeyDown={onGridKeyDown}
        >
          <thead>
            <tr>
              {weekdayOrder.map((weekday) => (
                <th
                  key={weekday}
                  scope="col"
                  abbr={locale.weekdayLongNames[weekday]}
                  className="text-muted-foreground h-8 w-8 p-0 text-center text-xs font-normal"
                >
                  {locale.weekdayShortNames[weekday]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week) => (
              <tr key={formatDate(week[0] ?? today)}>
                {week.map((day) => {
                  const key = formatDate(day);
                  const outside = day.month !== display.month;
                  const isSelected = sameDate(day, selected);
                  const isToday = sameDate(day, today);
                  const enabled = isDateInRange(day, min, max);
                  return (
                    <td key={key} role="gridcell" aria-selected={isSelected} className="p-0">
                      <button
                        type="button"
                        tabIndex={sameDate(day, active) ? 0 : -1}
                        disabled={!enabled}
                        aria-label={locale.formatFullDate(day)}
                        aria-current={isToday ? 'date' : undefined}
                        data-day={key}
                        data-outside={outside ? '' : undefined}
                        data-today={isToday ? '' : undefined}
                        data-selected={isSelected ? '' : undefined}
                        className={cn(
                          'flex size-8 items-center justify-center rounded-md border border-transparent text-sm tabular-nums transition-colors outline-none',
                          'hover:bg-accent hover:text-accent-foreground',
                          'focus-visible:ring-ring/50 focus-visible:ring-[3px]',
                          'disabled:pointer-events-none disabled:opacity-40',
                          outside && 'text-muted-foreground',
                          isToday && 'border-primary',
                          isSelected &&
                            'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground',
                        )}
                        onClick={() => {
                          setActive(day);
                          if (outside) setDisplay(monthOf(day));
                          onSelect(day, 'pick');
                        }}
                      >
                        {day.day}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
